import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, APIError, sessionStore } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { renderGoogleButton, signInWithApple } from '@/lib/oauth';
import { SignupBlockedModal } from '@/components/SignupBlockedModal';

type Mode = 'signin' | 'signup';

interface OAuthButtonsProps {
  mode: Mode;
  /**
   * Required when mode === 'signup'. Backend rejects first-time OAuth with
   * TERMS_REQUIRED if terms_accepted isn't sent. In signin mode, terms
   * acceptance was captured at original signup so we omit it.
   */
  termsAccepted?: boolean;
}

/**
 * Renders Google + Apple sign-in as a vertical stack, Google first.
 *
 * After a successful exchange with the backend we navigate to /home if the
 * user's profile is already complete, or to /auth/profile to collect DOB
 * + display_name on a dedicated screen.
 */
export function OAuthButtons({ mode, termsAccepted }: OAuthButtonsProps) {
  const navigate = useNavigate();
  const googleSlotRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'apple' | null>(null);
  const [signupsBlocked, setSignupsBlocked] = useState(false);

  // For signup we need terms accepted before letting the user start an OAuth
  // flow — otherwise the backend rejects the first-time signup. For signin
  // there's no gate.
  const gated = mode === 'signup' && !termsAccepted;

  // Mount Google's iframe button once, on mount. The button stays rendered
  // regardless of the terms gate so it's visible (in disabled styling) from
  // first paint; the parent's opacity + pointer-events: none below blocks
  // clicks until terms are accepted, so the awaited click can't resolve
  // during the gated state.
  useEffect(() => {
    if (!googleSlotRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await renderGoogleButton(googleSlotRef.current!);
        if (cancelled) return;
        await exchange('google', idToken);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof APIError && err.code === 'SIGNUPS_DISABLED') {
          setSignupsBlocked(true);
          return;
        }
        setError(friendlyError(err, 'oauthGoogle'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApple = async () => {
    setError(null);
    setBusy('apple');
    try {
      const idToken = await signInWithApple();
      await exchange('apple', idToken);
    } catch (err) {
      if (err instanceof APIError && err.code === 'SIGNUPS_DISABLED') {
        setSignupsBlocked(true);
      } else {
        setError(friendlyError(err, 'oauthApple'));
      }
    } finally {
      setBusy(null);
    }
  };

  async function exchange(provider: 'google' | 'apple', idToken: string) {
    const fn = provider === 'google' ? api.oauthGoogle : api.oauthApple;
    const body =
      mode === 'signup'
        ? { id_token: idToken, terms_accepted: true }
        : { id_token: idToken };
    try {
      const pair = await fn(body);
      sessionStore.set(pair.access, pair.refresh, pair.user);
      // Incomplete profiles → builder (its Basics + Age steps cover the
      // old /auth/profile screen). Completed → home.
      navigate(pair.user.profile_complete ? '/home' : '/builder');
    } catch (err) {
      // A signin-mode OAuth on an unrecognized account ends up here when the
      // backend asks for terms (first-time signup path). Bounce to signup
      // and stash the id_token so we don't force the user to re-auth.
      if (err instanceof APIError && err.code === 'TERMS_REQUIRED') {
        sessionStorage.setItem('pending_oauth', JSON.stringify({ provider, id_token: idToken }));
        navigate('/auth/signup');
        return;
      }
      throw err;
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          alignItems: 'stretch',
          opacity: gated ? 0.5 : 1,
          pointerEvents: gated ? 'none' : 'auto',
        }}
      >
        {/* Google's GIS iframe button. Width capped at ~400 by Google. */}
        <div ref={googleSlotRef} style={{ display: 'flex', justifyContent: 'center' }} />
        <button
          type="button"
          onClick={handleApple}
          disabled={busy !== null || gated}
          style={appleBtn}
          aria-label="Continue with Apple"
        >
          {busy === 'apple' ? (
            '…'
          ) : (
            <>
              <AppleLogo />
              <span>Continue with Apple</span>
            </>
          )}
        </button>
      </div>
      {error && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
          {error}
        </div>
      )}
      <SignupBlockedModal
        open={signupsBlocked}
        onClose={() => setSignupsBlocked(false)}
      />
    </div>
  );
}

const appleBtn: React.CSSProperties = {
  width: '100%',
  height: 44,
  borderRadius: 50,
  background: '#000',
  color: '#fff',
  fontWeight: 600,
  fontSize: 14,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

// Apple's brand-approved silhouette mark. Kept inline so it sits on the
// button without an extra HTTP request, sized to match the text cap-height.
function AppleLogo() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      // The logo's optical center sits a hair below geometric center.
      style={{ position: 'relative', top: -1 }}
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}
